import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import { toast } from 'react-toastify';
import { Typography } from '@mui/material';
import { addCategory, updateCategory } from '../../services/api/Category';
import { StyledCrossIconButton } from '../../components/component/ThemeComponent';

const useStyles = makeStyles({
	inputField: {
		marginBottom: '15px',
	},
	imagePreview: {
		width: '100%',
		height: '200px',
		marginTop: '10px',
		objectFit: 'contain',
	},
});

const VisuallyHiddenInput = styled('input')({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: 1,
	overflow: 'hidden',
	position: 'absolute',
	bottom: 0,
	left: 0,
	whiteSpace: 'nowrap',
	width: 1,
});

interface CreateCategoriesProps {
	open: boolean;
	handleClose: () => void;
	fetchCategories: () => void;
	category: any | null;
}

const CreateCategories: React.FC<CreateCategoriesProps> = ({
	open,
	handleClose,
	fetchCategories,
	category,
}) => {
	const classes = useStyles();
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [image, setImage] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [videoPreview, setVideoPreview] = useState<string | null>(null);
	const [nameError, setNameError] = useState('');
	const [descriptionError, setDescriptionError] = useState('');
	const [imageError, setImageError] = useState('');
	const [loading, setLoading] = useState(false);
	const [videoFile, setVideoFile] = useState<File | null>(null);

	useEffect(() => {
		if (category) {
			setName(category.categoryName.name);
			setDescription(category.categoryName.description);
			setImagePreview(
				typeof category?.categoryName?.categoryImage === 'string'
					? getImageUrl(category.categoryName.categoryImage)
					: null
			);
			setVideoPreview(
				typeof category?.categoryName?.categoryVideo === 'string'
					? category.categoryName.categoryVideo
					: null
			);
		} else {
			setName('');
			setDescription('');
			setImage(null);
			setImagePreview(null);
			setVideoPreview(null);
		}
		setNameError('');
		setDescriptionError('');
		setImageError('');
	}, [category, open]);

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file && file.size <= 1024 * 1024 && ['image/jpeg', 'image/png'].includes(file.type)) {
			setImage(file);
			setImagePreview(URL.createObjectURL(file));
			setImageError('');
		} else {
			alert('Image must be JPEG or PNG and less than 1MB');
			setImageError('Image is required');
		}
	};

	const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (
			file &&
			file.size <= 5 * 1024 * 1024 &&
			['video/mp4', 'video/webm'].includes(file.type)
		) {
			setVideoFile(file); // ✅ Store the actual file
			setVideoPreview(URL.createObjectURL(file)); // Optional preview
		} else {
			alert('Video must be MP4 or WebM and less than 5MB');
		}
	};

	const validateForm = () => {
		let valid = true;
		if (!name) {
			setNameError('Name is required');
			valid = false;
		} else {
			setNameError('');
		}
		if (!description) {
			setDescriptionError('Description is required');
			valid = false;
		} else {
			setDescriptionError('');
		}
		if (!image && !category?.categoryName?.categoryImage) {
			setImageError('Image is required');
			valid = false;
		}
		return valid;
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (validateForm()) {
			const formDataToSend = new FormData();
			formDataToSend.append('name', name);
			formDataToSend.append('description', description);

			if (image) {
				formDataToSend.append('categoryImage', image);
			}
			if (videoFile) {
				formDataToSend.append('categoryVideo', videoFile); // ✅ Correct object type
			}
			try {
				setLoading(true);
				if (category) {
					const response = await updateCategory(
						formDataToSend,
						category.categoryName.slug.toString()
					);
					if (response.success) {
						toast.success('Category updated successfully');
					} else {
						toast.error('Failed to update category');
					}
				} else {
					const newCategory = await addCategory(formDataToSend);
					if (newCategory.success) {
						toast.success('Category added successfully.');
					} else {
						toast.error(`${newCategory.data.errors}`);
					}
				}
				handleClose();
				fetchCategories();
			} catch (error) {
				toast.error('Failed to save category');
			} finally {
				setLoading(false);
			}
		}
	};

	const handleCancel = () => {
		setName('');
		setDescription('');
		setImage(null);
		setImagePreview(null);
		setVideoPreview(null);
		setNameError('');
		setDescriptionError('');
		setImageError('');
		handleClose();
	};

	const getImageUrl = (path: string) => {
		if (!path) return '';
		if (path.startsWith('http')) return path;
		return `${process.env.REACT_APP_BASE_IMAGE_URL}${path}`;
	};

	return (
		<>
			<Dialog open={open} onClose={handleCancel}>
				<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<span>{category ? 'Edit Category' : 'Add Category'}</span>
					<StyledCrossIconButton onClick={handleCancel}>
						<CloseIcon />
					</StyledCrossIconButton>
				</DialogTitle>
				<DialogContent>
					<TextField
						margin="dense"
						label="Category Name"
						fullWidth
						value={name}
						onChange={(e) => setName(e.target.value)}
						className={classes.inputField}
						size="small"
						error={!!nameError}
						helperText={nameError}
						required
					/>
					<TextField
						margin="dense"
						label="Description"
						fullWidth
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						className={classes.inputField}
						size="small"
						error={!!descriptionError}
						helperText={descriptionError}
						required
					/>
					<Typography sx={{ margin: '0.5rem 0px' }}>
						<Button
							component="label"
							role={undefined}
							variant="contained"
							tabIndex={-1}
							startIcon={<ImageIcon />}
						>
							Upload Image file
							<VisuallyHiddenInput
								type="file"
								onChange={handleImageChange}
								accept="image/jpeg,image/png"
							/>
						</Button>
						<br />
						{imageError && <div style={{ color: 'red' }}>{imageError}</div>}
						{imagePreview ? (
							<img
								src={imagePreview}
								className={classes.imagePreview}
								alt="Category Preview"
								style={{ width: '250px', height: '200px', marginTop: '5px' }}
							/>
						) : (
							<span
								style={{ color: '#8f8e8e', paddingLeft: '5px', fontSize: '14px' }}
							>
								Upload the image up to 1MB Size and should be JPEG or PNG
							</span>
						)}
					</Typography>
					<Typography sx={{ margin: '0.5rem 0px' }}>
						<Button
							component="label"
							role={undefined}
							variant="contained"
							tabIndex={-1}
							startIcon={<VideocamIcon />}
						>
							Upload Video file
							<VisuallyHiddenInput
								type="file"
								onChange={handleVideoChange}
								accept="video/mp4,video/webm"
							/>
						</Button>
						<br />
						{videoPreview ? (
							<video
								src={videoPreview}
								controls
								style={{ width: '250px', height: '200px', marginTop: '5px' }}
							>
								<track kind="captions" srcLang="en" src="captions.vtt" default />
							</video>
						) : (
							<span
								style={{ color: '#8f8e8e', paddingLeft: '5px', fontSize: '14px' }}
							>
								Upload the video up to 5MB Size and should be MP4 or WebM
							</span>
						)}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancel}>Cancel</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						color="primary"
						disabled={loading}
					>
						{loading ? 'Saving...' : 'Save'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default CreateCategories;
